// import { createRoot } from "react-dom/client";
import maplibregl from "maplibre-gl";
import { createMarkerPopup } from "./mapMarkerPopup";
import { selectPointsForMarker } from "./mapCoordinatesUtils";
import {
  convertIconSpecToData,
  iconToSvgString,
  iconToSvgElement,
} from "../../../components/icons/iconToSvg";
// import { Icon } from "../../../components/icons/Icon";
import { IconMap } from "../components/icons/IconMap";

// import { iconFromSegment } from "../utils/iconBuilder";
// import { iconSvg } from "../utils/icons-svg";

/**
 * Build 1..N markers for a segment using direct inline SVG.
 *
 * @param {Object} opts
 * @param {maplibregl.Map} opts.map
 * @param {Object} opts.segment
 * @param {boolean} [opts.draggable=false]
 * @param {'primary'|'departure'|'arrival'|'both'} [opts.point='primary']
 * @param {Object|boolean} [opts.popup] - true => default; object => custom; falsy => none
 * @param {Function} [opts.onDragEnd]
 * @param {Object} [opts.iconOverride]  - { name, size, strokeWidth, className }
 *
 * @returns {Array<{ marker, popup?: maplibregl.Popup, destroy: Function, role: string, id: any }>}
/**
 * IconMap returns this   {
    "viewBox": "0 0 512 512",
    "path": {
        "key": null,
        "props": {
            "children": {
                "type": "path",
                "key": null,
                "props": {
                    "d": "M186.62,464H160a16,16,0,0,1-14.57-22.6l64.46-142.25L113.1,297,77.8,339.77C71.07,348.23,65.7,352,52,352H34.08a17.66,17.66,0,0,1-14.7-7.06c-2.38-3.21-4.72-8.65-2.44-16.41l19.82-71c.15-.53.33-1.06.53-1.58a.38.38,0,0,0,0-.15,14.82,14.82,0,0,1-.53-1.59L16.92,182.76c-2.15-7.61.2-12.93,2.56-16.06a16.83,16.83,0,0,1,13.6-6.7H52c10.23,0,20.16,4.59,26,12l34.57,42.05,97.32-1.44-64.44-142A16,16,0,0,1,160,48h26.91a25,25,0,0,1,19.35,9.8l125.05,152,57.77-1.52c4.23-.23,15.95-.31,18.66-.31C463,208,496,225.94,496,256c0,9.46-3.78,27-29.07,38.16-14.93,6.6-34.85,9.94-59.21,9.94-2.68,0-14.37-.08-18.66-.31l-57.76-1.54-125.36,152A25,25,0,0,1,186.62,464Z"
                },
                "_owner": null,
                "_store": {}
            }
        },
        "_owner": null,
        "_store": {}
    }
}
 *
 * @export
 * @param {*} {
 *   map,
 *   segment,
 *   draggable = false,
 *   point = "primary",
 *   popup,
 *   onDragEnd,
 *   iconOverride,
 * }
 * @return {*} 
 */

export function createMapMarker({
  map,
  segment,
  markerGroup, // NEW: optional grouped data for de-duplicated markers
  draggable = false,
  point = "primary",
  addPopup,
  onDragEnd,
  iconOverride,
}) {
  if (!map || !segment) return [];

  const wrappers = [];

  // -------------------------------
  // 1) Resolve point(s) & position
  // -------------------------------

  let pts = [];
  let lng = null;
  let lat = null;

  if (markerGroup) {
    // New grouped behavior:
    // - One physical location for this marker
    lng = markerGroup.lng;
    lat = markerGroup.lat;

    // pts is kept mostly for backwards compatibility (e.g. drag handlers),
    // but all pts share the same lng/lat in a grouped marker.
    pts = markerGroup.points.map(({ role }) => ({
      lng,
      lat,
      role,
    }));

    // If there are *no* roles for some reason, bail out
    if (!pts.length) return [];
  } else {
    // Legacy behavior: use selectPointsForMarker per segment
    /*
     * pts format
     * [{"lng": -85.5406712,"lat": 10.6006094,"role": "departure"},
     *  {"lng": -104.7776748,"lat": 39.8563189,"role": "arrival"}]
     *  OR
     * [{"lng": -85.04160201693912,"lat": 10.713062032196746,"role": "location"}]
     */
    pts = selectPointsForMarker(segment, point); // [{lng,lat,role}, ...]
    if (!pts.length) return [];

    // For legacy case we still may create multiple markers — handled below
  }

  // -------------------------------
  // 2) Grouped marker path
  // -------------------------------
  if (markerGroup) {
    // One marker for this whole group/location
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      console.warn("[Map] skipped invalid grouped point", {
        segmentId: segment?.id,
        markerGroupKey: markerGroup.key,
        lng,
        lat,
      });
      return [];
    }

    // Marker element
    const mkrEl = document.createElement("div");
    mkrEl.className = `map-marker ${segment.type}`;

    // Icon selection logic (unchanged, just used once per group)
    if (segment?.type === "transportation" && segment?.mode) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.mode],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "accommodation" && segment?.spaceType) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.spaceType],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "activity" && segment?.activityType) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.activityType],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "tracks") {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.type],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else {
      // Dot fallback when no icon
      mkrEl.classList.add("map-marker--dot");
    }

    // Create the marker at the grouped location
    const marker = new maplibregl.Marker({ element: mkrEl, draggable })
      .setLngLat([lng, lat])
      .addTo(map);

    // Drag callback — for grouped markers, we just report the first role
    // (you can expand this later if you want multi-role behavior)
    const primaryRole = pts[0]?.role;
    if (draggable && typeof onDragEnd === "function") {
      marker.on("dragend", () => {
        try {
          onDragEnd({
            lngLat: marker.getLngLat(),
            marker,
            segment,
            role: primaryRole,
            markerGroup,
          });
        } catch {}
      });
    }

    // Optional popup — pass markerGroup so popup can show all arrivals/departures
    let popupInstance;
    if (addPopup) {
      popupInstance = createMarkerPopup({
        segment,
        markerGroup, // NEW: gives popup access to all arrivals/departures here
        onDelete: () => marker.onDelete?.({ segment, marker, markerGroup }),
        onEdit: () => marker.onEdit?.({ segment, marker, markerGroup }),
        onAddImage: () => marker.onAddImage?.({ segment, marker, markerGroup }),
      });

      marker.setPopup(popupInstance);
    }

    const destroy = () => {
      try {
        popupInstance?.remove();
      } catch {}
      try {
        marker.remove();
      } catch {}
    };

    wrappers.push({
      marker,
      popup: popupInstance,
      destroy,
      // Keep single role for backwards compat, plus allRoles if you need them
      role: primaryRole,
      allRoles: pts.map((p) => p.role),
      id: segment.id,
      markerGroup,
    });

    return wrappers;
  }

  // -------------------------------
  // 3) Legacy per-point path
  // -------------------------------
  // pts include segments that have multiple coordinates, like air travel.
  // so iterating pts creates each coordinate (can duplicate icons with dep/arr)
  for (const pt of pts) {
    if (!Number.isFinite(pt.lng) || !Number.isFinite(pt.lat)) {
      console.warn("[Map] skipped invalid point", {
        segmentId: segment?.id,
        role: pt.role,
        lng: pt.lng,
        lat: pt.lat,
      });
      continue;
    }

    // Marker element
    const mkrEl = document.createElement("div");
    mkrEl.className = `map-marker ${segment.type}`;

    if (segment?.type === "transportation" && segment?.mode) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.mode],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "accommodation" && segment?.spaceType) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.spaceType],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "activity" && segment?.activityType) {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.activityType],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else if (segment?.type === "tracks") {
      const svgData = convertIconSpecToData({
        iconSpec: IconMap[segment.type],
        size: 22,
      });
      const iconEl = iconToSvgElement(svgData);
      mkrEl.append(iconEl);
    } else {
      // Dot fallback when no icon
      mkrEl.classList.add("map-marker--dot");
    }

    // Create the marker
    const marker = new maplibregl.Marker({ element: mkrEl, draggable })
      .setLngLat([pt.lng, pt.lat])
      .addTo(map);

    // Drag callback
    if (draggable && typeof onDragEnd === "function") {
      marker.on("dragend", () => {
        try {
          onDragEnd({
            lngLat: marker.getLngLat(),
            marker,
            segment,
            role: pt.role,
          });
        } catch {}
      });
    }

    // Segment addPopup (true => use defaults; object => merge)
    let popupInstance;
    if (addPopup) {
      popupInstance = createMarkerPopup({
        segment,
        onDelete: () => marker.onDelete?.({ segment, marker }),
        onEdit: () => marker.onEdit?.({ segment, marker }),
        onAddImage: () => marker.onAddImage?.({ segment, marker }),
      });
      console.debug(popupInstance);
      marker.setPopup(popupInstance);
    }

    const destroy = () => {
      try {
        popupInstance?.remove();
      } catch {}
      try {
        marker.remove();
      } catch {}
    };

    wrappers.push({
      marker,
      popup: popupInstance,
      destroy,
      role: pt.role,
      id: segment.id,
    });
  }
  return wrappers;
}

//previous
// export function createMapMarker({
//   map,
//   segment,
//   draggable = false,
//   point = "primary",
//   addPopup,
//   onDragEnd,
//   iconOverride,
// }) {
//   if (!map || !segment) return [];
//   // console.debug(segment.type);

//   /* pts format

//    * [{"lng": -85.5406712,"lat": 10.6006094,"role": "departure"},
//    * {"lng": -104.7776748,"lat": 39.8563189,"role": "arrival"}]
//    *  OR
//    * [{"lng": -85.04160201693912,"lat": 10.713062032196746,"role": "location"}]
//    */
//   const pts = selectPointsForMarker(segment, point); // [{lng,lat,role}, ...]
//   // console.debug(segment);
//   if (!pts.length) return [];
//   // console.debug(pts);
//   const wrappers = [];

//   // pts include segments that have multiple coordinates, like air travel.
//   //   so iterating pts creates each coordinate (duplicates icons with departure and arrival)
//   for (const pt of pts) {
//     // {
//     //     "lng": -116.202316,
//     //     "lat": 43.615021,
//     //     "role": "arrival"
//     // }
//     // console.log(pt);
//     // Guard: skip invalid numbers to avoid setLngLat(null)
//     if (!Number.isFinite(pt.lng) || !Number.isFinite(pt.lat)) {
//       console.warn("[Map] skipped invalid point", {
//         segmentId: segment?.id,
//         role: pt.role,
//         lng: pt.lng,
//         lat: pt.lat,
//       });
//       continue;
//     }

//     // Marker element
//     const mkrEl = document.createElement("div");
//     mkrEl.className = `map-marker ${segment.type}`;

//     // Icon (override > inferred from type/subType/role)
//     // const iconCfg = {
//     //   //iconOverride || {
//     //   name: "airplane", //pt.subType,
//     //   size: 22,
//     //   className: "map-marker--icon",
//     // };

//     // const cfg =
//     //   iconOverride ||
//     //   (() => {
//     //     const i = iconFromSegment(segment, pt.role);
//     //     return {
//     //       name: i.name,
//     //       size: i.size,
//     //       strokeWidth: i.strokeWidth,
//     //       className: "map-marker__icon",
//     //     };
//     //   })();

//     // transportation
//     if (segment?.type === "transportation" && segment?.mode) {
//       //   mkrEl.classList.add("map-marker--icon");

//       //   el.innerHTML = iconToSvgString(iconCfg.name, {
//       //     size: iconCfg.size,
//       //     strokeWidth: iconCfg.strokeWidth,
//       //   });

//       // svg html NS element
//       const svgData = convertIconSpecToData({
//         iconSpec: IconMap[segment.mode],
//         size: 22,
//       });
//       // console.log(svgData);
//       const iconEl = iconToSvgElement(svgData);

//       //   if (iconCfg.className) {
//       //     // && iconEl.firstElementChild?.classList) {
//       //     iconEl.classList.add(iconCfg.className);
//       //   }
//       mkrEl.append(iconEl);

//       // react render icon spec
//       // const svgEl =
//       //   ReactDOM.render(<Icon icon="airplane" />, el);
//       //   const divEl = document.createElement("div");
//       //   const root = createRoot(mkrEl);
//       //   root.render(<Icon icon="airplane" />);

//       // To clean up:
//       // root.unmount();

//       // Set color via CSS currentColor; ensure color isn't transparent
//       //   mkrEl.style.color = mkrEl.style.color || "currentColor";
//     }
//     //else {
//     //   // Dot fallback when no icon
//     //   mkrEl.classList.add("map-marker--dot");
//     // }

//     // accommodation
//     else if (segment?.type === "accommodation" && segment?.spaceType) {
//       // svg html NS element
//       const svgData = convertIconSpecToData({
//         iconSpec: IconMap[segment.spaceType],
//         size: 22,
//       });
//       const iconEl = iconToSvgElement(svgData);
//       mkrEl.append(iconEl);
//     } else if (segment?.type === "activity" && segment?.activityType) {
//       // svg html NS element
//       const svgData = convertIconSpecToData({
//         iconSpec: IconMap[segment.activityType],
//         size: 22,
//       });
//       const iconEl = iconToSvgElement(svgData);
//       mkrEl.append(iconEl);
//     } else if (segment?.type === "tracks") {
//       // svg html NS element
//       const svgData = convertIconSpecToData({
//         iconSpec: IconMap[segment.type],
//         size: 22,
//       });
//       const iconEl = iconToSvgElement(svgData);
//       mkrEl.append(iconEl);
//     } else {
//       // Dot fallback when no icon
//       mkrEl.classList.add("map-marker--dot");
//     }

//     // Create the marker
//     const marker = new maplibregl.Marker({ element: mkrEl, draggable })
//       .setLngLat([pt.lng, pt.lat])
//       .addTo(map);

//     // Drag callback
//     if (draggable && typeof onDragEnd === "function") {
//       marker.on("dragend", () => {
//         try {
//           onDragEnd({
//             lngLat: marker.getLngLat(),
//             marker,
//             segment,
//             role: pt.role,
//           });
//         } catch {}
//       });
//     }

//     // Optional addPopup (true => use defaults; object => merge)
//     let popupInstance;

//     if (addPopup) {
//       //   const defaults = {
//       //     title:
//       //       segment.provider ||
//       //       segment.name ||
//       //       (pt.role === "departure"
//       //         ? "Departure"
//       //         : pt.role === "arrival"
//       //         ? "Arrival"
//       //         : "Location"),
//       //     description: segment.description || "",
//       //   };

//       //   const cfgPopup =
//       //     typeof popup === "object" ? { ...defaults, ...popup } : defaults;

//       // const popupCfg = {
//       //     title: renderTitle(segment ?? "Info")
//       // }

//       popupInstance = createMarkerPopup({
//         // ...cfgPopup,
//         segment,
//         onDelete: () => marker.onDelete?.({ segment, marker }), //() => cfgPopup.onDelete?.({ segment, marker, role: pt.role }),
//         onEdit: () => marker.onEdit?.({ segment, marker }), //() => cfgPopup.onEdit?.({ segment, marker, role: pt.role }),
//         onAddImage: () => marker.onAddImage?.({ segment, marker }), //() =>cfgPopup.onAddImage?.({ segment, marker, role: pt.role }),
//       });

//       marker.setPopup(popupInstance);

//       //DB not sure what this code does
//       //   if (cfgPopup.openOnCreate) {
//       //     popupInstance.addTo(map).setLngLat([pt.lng, pt.lat]);
//       //   }
//     }

//     const destroy = () => {
//       try {
//         popupInstance?.remove();
//       } catch {}
//       try {
//         marker.remove();
//       } catch {}
//     };

//     wrappers.push({
//       marker,
//       popup: popupInstance,
//       destroy,
//       role: pt.role,
//       id: segment.id,
//     });
//   }

//   return wrappers;
// }

// import maplibregl from "maplibre-gl";
// import { createMarkerPopup } from "./MarkerPopupComponent";
// import { selectPointsForMarker } from "../../utils/segmentGeo";
// // import { iconFromSegment } from "../utils/iconBuilder";
// // import { iconSvg } from "../utils/icons-svg";
// import { iconToSvgString } from "../../../../components/icons/iconToSvgString";

// /**
//  * Builds 1..N markers for a segment using direct SVG.
//  */
// export function createMapMarker({
//   map,
//   segment,
//   draggable = false,
//   point = "primary", // 'primary' | 'departure' | 'arrival' | 'both'
//   popup, // true => default; object => config; falsy => none
//   onDragEnd,
//   iconOverride, // { name, size, strokeWidth, className }
// }) {
//   if (!map || !segment) return [];

//   const pts = selectPointsForMarker(segment, point);
//   if (!pts.length) return [];
//   console.log(pts);

//   const wrappers = [];
//   for (const pt of pts) {
//     // ⬇︎ guard: skip invalid points to avoid setLngLat(null)
//     if (!Number.isFinite(pt.lng) || !Number.isFinite(pt.lat)) {
//       console.warn("[Map] skipped invalid point", {
//         segmentId: segment?.id,
//         role: pt.role,
//         lng: pt.lng,
//         lat: pt.lat,
//       });
//       continue;
//     }

//     const el = document.createElement("div");
//     el.className = "map-marker";

//     // icon selection
//     const iconCfg = iconOverride || {
//       name: pt.subType,
//       size: 22,
//       className: "map-marker--icon",
//     };
//     //   (() => {
//     //     const i = iconFromSegment(segment, pt.role);
//     //     return { name:key, size };
//     //     return {
//     //       name: i.name,
//     //       size: i.size,
//     //       strokeWidth: i.strokeWidth,
//     //       className: "map-marker__icon",
//     //     };
//     //   })();

//     if (iconCfg?.name) {
//       el.classList.add("map-marker--icon");
//       el.innerHTML = iconToSvgString(iconCfg.name, {
//         size: iconCfg.size,
//         strokeWidth: iconCfg.strokeWidth,
//       });
//       if (iconCfg.className)
//         el.firstElementChild?.classList?.add(iconCfg.className);
//       el.setAttribute("aria-hidden", "true");
//     } else {
//       el.classList.add("map-marker--dot");
//     }

//     const marker = new maplibregl.Marker({ element: el, draggable })
//       .setLngLat([pt.lng, pt.lat])
//       .addTo(map);

//     if (draggable && typeof onDragEnd === "function") {
//       marker.on("dragend", () => {
//         try {
//           onDragEnd({
//             lngLat: marker.getLngLat(),
//             marker,
//             segment,
//             role: pt.role,
//           });
//         } catch {}
//       });
//     }

//     let popupInstance;
//     if (popup) {
//       const defaults = {
//         title:
//           segment.title ||
//           segment.name ||
//           (pt.role === "departure"
//             ? "Departure"
//             : pt.role === "arrival"
//             ? "Arrival"
//             : "Location"),
//         description: segment.description || "",
//       };
//       const cfg =
//         typeof popup === "object" ? { ...defaults, ...popup } : defaults;
//       popupInstance = createMarkerPopup({
//         ...cfg,
//         onDelete: () => cfg.onDelete?.({ segment, marker, role: pt.role }),
//         onEdit: () => cfg.onEdit?.({ segment, marker, role: pt.role }),
//         onAddImage: () => cfg.onAddImage?.({ segment, marker, role: pt.role }),
//       });
//       marker.setPopup(popupInstance);
//       if (cfg.openOnCreate)
//         popupInstance.addTo(map).setLngLat([pt.lng, pt.lat]);
//     }

//     const destroy = () => {
//       try {
//         popupInstance?.remove();
//       } catch {}
//       try {
//         marker.remove();
//       } catch {}
//     };

//     wrappers.push({
//       marker,
//       popup: popupInstance,
//       destroy,
//       role: pt.role,
//       id: segment.id,
//     });
//   }
//   return wrappers;
// }

// // previous
// // // import maplibregl from "maplibre-gl";
// // import React from "react";
// // import { createRoot } from "react-dom/client";
// // import { Icon } from "../../../../components/icons/Icon"; // adjust path to your Generic Icon
// // import { createMarkerPopup } from "./MarkerPopupComponent";
// // import { selectPointsForMarker } from "../../utils/segmentGeo";
// // import { iconFromSegment } from "../../utils/iconBuilder";

// // // Defer unmount to avoid React 18 "synchronous unmount during render" warning
// // function safeUnmountReactRoot(root) {
// //   if (!root) return;
// //   // Prefer microtask; fall back to rAF if you want:
// //   Promise.resolve().then(() => {
// //     try {
// //       root.unmount();
// //     } catch {}
// //   });
// //   // Or: requestAnimationFrame(() => { try { root.unmount(); } catch {} });
// // }

// // /**
// //  * Build 1..N markers for a segment.
// //  *
// //  * @param {Object} opts
// //  * @param {maplibregl.Map} opts.map
// //  * @param {Object} opts.segment
// //  * @param {boolean} [opts.draggable=false]
// //  * @param {'primary'|'departure'|'arrival'|'both'} [opts.point='primary']
// //  * @param {Object|boolean} [opts.popup] - truthy to build default, object to customize, falsy to skip
// //  * @param {Function} [opts.onDragEnd]
// //  * @param {Object} [opts.iconOverride]  - if provided, bypass iconFromSegment
// //  *
// //  * @returns {Array<{ marker, popup?, destroy:Function, element }>}
// //  */
// // export function createMapMarker({
// //   map,
// //   segment,
// //   draggable = false,
// //   point = "primary",
// //   popup,
// //   onDragEnd,
// //   iconOverride,
// // }) {
// //   if (!map || !segment) return [];

// //   // Decide which coordinates to plot
// //   const points = selectPointsForMarker(segment, point); // [{lng,lat, role}, ...]
// //   if (!points.length) return [];

// //   const wrappers = [];

// //   for (const pt of points) {
// //     const el = document.createElement("div");
// //     el.className = "map-marker";

// //     // Decide icon (override > fromSegment > fallback dot)
// //     const iconCfg = iconOverride || iconFromSegment(segment, pt.role);
// //     if (iconCfg && iconCfg.icon) {
// //       el.classList.add("map-marker--icon");
// //       const root = createRoot(el);
// //       root.render(
// //         React.createElement(Icon, {
// //           icon: iconCfg.icon,
// //           size: iconCfg.size ?? 22,
// //           strokeWidth: iconCfg.strokeWidth,
// //           className: iconCfg.className ?? "map-marker__icon",
// //           width: iconCfg.width,
// //           height: iconCfg.height,
// //         })
// //       );
// //       el.__root = root;
// //     } else {
// //       // dot style
// //       el.classList.add("map-marker--dot");
// //     }

// //     const marker = new maplibregl.Marker({ element: el, draggable })
// //       .setLngLat([pt.lng, pt.lat])
// //       .addTo(map);

// //     if (draggable && typeof onDragEnd === "function") {
// //       marker.on("dragend", () => {
// //         try {
// //           onDragEnd({
// //             lngLat: marker.getLngLat(),
// //             marker,
// //             segment,
// //             role: pt.role,
// //           });
// //         } catch {}
// //       });
// //     }

// //     let popupInstance;
// //     if (popup) {
// //       const defaults = {
// //         title:
// //           segment.title ||
// //           segment.name ||
// //           (pt.role === "departure"
// //             ? "Departure"
// //             : pt.role === "arrival"
// //             ? "Arrival"
// //             : "Location"),
// //         description: segment.description || "",
// //       };
// //       const cfg =
// //         typeof popup === "object" ? { ...defaults, ...popup } : defaults;
// //       popupInstance = createMarkerPopup({
// //         ...cfg,
// //         onDelete: () => cfg.onDelete?.({ segment, marker, role: pt.role }),
// //         onEdit: () => cfg.onEdit?.({ segment, marker, role: pt.role }),
// //         onAddImage: () => cfg.onAddImage?.({ segment, marker, role: pt.role }),
// //       });
// //       marker.setPopup(popupInstance);
// //       if (cfg.openOnCreate) {
// //         popupInstance.addTo(map);
// //         popupInstance.setLngLat([pt.lng, pt.lat]);
// //       }
// //     }

// //     const destroy = () => {
// //       try {
// //         popupInstance?.remove();
// //       } catch {}

// //       try {
// //         marker.remove();
// //       } catch {}

// //       // If we rendered a React Icon into the marker, unmount it *asynchronously*
// //       if (el.__root) {
// //         const root = el.__root;
// //         el.__root = null; // guard against double calls
// //         safeUnmountReactRoot(root); // <-- defer unmount
// //       }
// //     };

// //     // previous
// //     // const destroy = () => {
// //     //   try {
// //     //     popupInstance?.remove();
// //     //   } catch {}
// //     //   try {
// //     //     marker.remove();
// //     //   } catch {}
// //     //   if (el.__root) {
// //     //     try {
// //     //       el.__root.unmount?.();
// //     //     } catch {}
// //     //     delete el.__root;
// //     //   }
// //     // };

// //     wrappers.push({
// //       marker,
// //       popup: popupInstance,
// //       destroy,
// //       element: el,
// //       role: pt.role,
// //       id: segment.id,
// //     });
// //   }

// //   return wrappers;
// // }

// // src/features/map/components/MapMarkerComponent.js
// // import maplibregl from "maplibre-gl";
// // import React from "react";
// // import { createRoot } from "react-dom/client";
// // // ⬇️ Adjust import path to your actual Generic Icon component
// // import { Icon } from "../../../../components/icons/Icon"; // e.g., src/components/Icon.jsx
// // import { createMarkerPopup } from "./MarkerPopupComponent";
// // import { IconMap } from "../icons/IconMap";

// // /**
// //  * Create a MapLibre Marker with optional custom icon, draggable, and popup.
// //  *
// //  * @param {Object} opts
// //  * @param {maplibregl.Map} opts.map        - MapLibre map instance
// //  * @param {number} opts.lng
// //  * @param {number} opts.lat
// //  * @param {boolean} [opts.draggable=false]
// //  *
// //  * @param {Object} [opts.icon]             - Render your Generic Icon into the marker
// //  *   @param {*} [opts.icon.icon]           - The icon mapping/key your Generic Icon expects
// //  *   @param {number} [opts.icon.size=22]
// //  *   @param {number} [opts.icon.strokeWidth]
// //  *   @param {string} [opts.icon.className]
// //  *   @param {number} [opts.icon.width]     - forwarded to Icon if supported
// //  *   @param {number} [opts.icon.height]
// //  *
// //  * @param {Object|false} [opts.popup]      - Pass an object for popup, or false/undefined to skip
// //  *   @param {string} [opts.popup.title]
// //  *   @param {string} [opts.popup.description]
// //  *   @param {boolean} [opts.popup.closeOnClick]
// //  *   @param {number|Object} [opts.popup.offset]
// //  *   @param {boolean} [opts.popup.openOnCreate=false]
// //  *   @param {Function} [opts.popup.onClose]
// //  *   @param {Function} [opts.popup.onDelete]
// //  *   @param {Function} [opts.popup.onEdit]
// //  *   @param {Function} [opts.popup.onAddImage]
// //  *
// //  * @param {Function} [opts.onDragEnd]      - Receives { lngLat, marker }
// //  *
// //  * @returns {{ marker: maplibregl.Marker, popup?: maplibregl.Popup, destroy: Function }}
// //  */
// // export function createMapMarker({
// //   map,
// //   segment,
// //   //   type,
// //   //   subType,
// //   //   lng,
// //   //   lat,
// //   draggable = false,
// //   //   icon,
// //   popup,
// //   onDragEnd,
// // }) {
// //   if (!map) throw new Error("createMapMarker: `map` is required");

// //   // Build the marker element
// //   const el = document.createElement("div");
// //   el.className = "map-marker"; // keep your existing dot styles as fallback

// //   // If a Generic Icon was provided, render it into the element
// //   console.log(segment);
// //   if (segment && segment.subType) {
// //     el.classList.add("map-marker--icon");
// //     // Render your Icon React component into this element
// //     const root = createRoot(el);
// //     root.render(
// //       React.createElement(Icon, {
// //         icon: IconMap[segment?.subType || "default"],
// //         size: 22,
// //         // strokeWidth: icon.strokeWidth,
// //         // className: icon.className ?? "map-marker__icon",
// //         width: 22, //icon.width,
// //         height: 22, //icon.height,
// //       })
// //     );
// //     // Keep a disposer to unmount the Icon cleanly
// //     el.__root = root;
// //   }

// //   let marker = null;
// //   // Create the marker
// //   if (segment.type === "transportation" && segment.subType === "airplane") {
// //     marker = new maplibregl.Marker({ element: el, draggable })
// //       .setLngLat([
// //         segment?.coordinates?.departure?.longitude,
// //         segment?.coordinates?.departure?.latitude,
// //       ])
// //       .addTo(map);
// //   } else {
// //     marker = new maplibregl.Marker({ element: el, draggable })
// //       .setLngLat([
// //         segment?.coordinates?.longitude,
// //         segment?.coordinates?.latitude,
// //       ])
// //       .addTo(map);
// //   }
// //   // Drag events
// //   if (draggable && typeof onDragEnd === "function") {
// //     marker.on("dragend", () => {
// //       try {
// //         const lngLat = marker.getLngLat();
// //         onDragEnd({ lngLat, marker });
// //       } catch {}
// //     });
// //   }

// //   // Optional popup
// //   let popupInstance;
// //   if (popup && typeof popup === "object") {
// //     popupInstance = createMarkerPopup({
// //       title: popup.title,
// //       description: popup.description,
// //       closeOnClick: popup.closeOnClick,
// //       offset: popup.offset,
// //       onClose: popup.onClose,
// //       onDelete: popup.onDelete,
// //       onEdit: popup.onEdit,
// //       onAddImage: popup.onAddImage,
// //     });
// //     marker.setPopup(popupInstance);
// //     if (popup.openOnCreate) {
// //       popupInstance.addTo(map); // show immediately at marker position
// //       popupInstance.setLngLat([
// //         segment?.coordinates?.longitude,
// //         segment?.coordinates?.latitude,
// //       ]);
// //     }
// //   }

// //   // Return helpers
// //   const destroy = () => {
// //     try {
// //       popupInstance?.remove();
// //       marker?.remove();
// //       if (el.__root) {
// //         el.__root.unmount?.();
// //         delete el.__root;
// //       }
// //     } catch {}
// //   };

// //   return { marker, popup: popupInstance, destroy, element: el };
// // }
